/**
 * Which layers the shell keeps mounted, and in what state (`D193`, `D225`).
 *
 * The URL names at most one sheet and one overlay. A layer the URL stops
 * naming is not unmounted at once:
 *
 * - **Covered.** When the layer that replaced it in its slot was PUSHED
 *   later — an exercise's details opened from the builder, from the
 *   catalogue, from the draft review — it stays mounted and open underneath,
 *   exactly as it was: its scroll, its filters, what it holds. Back closes
 *   the layer on top and uncovers it, the same instance. It stays covered
 *   while a later layer fills its slot, and leaves the moment none does or
 *   history comes back to where it was pushed and names something else.
 * - **Leaving.** Otherwise it stays, `present: false`, holding the search
 *   it last saw, until its exit reports done or the safety timeout removes
 *   it. A layer the URL names again while it is still leaving is revived in
 *   place — same entry, same component instance — so Back followed by
 *   Forward reverses the exit instead of remounting.
 *
 * A layer is known by its name AND the depth it was pushed at
 * (`mpgOpenedAt`, `app/layers.ts`): the exercise opened from an exercise is
 * a second layer over the first, while the coach pushed over the builder
 * leaves the builder the same layer.
 *
 * Pure, so the bookkeeping is tested without a router or a DOM.
 */

export interface RetainedLayer<V, S> {
  /** The history depth this layer was pushed at (0: a cold entry). */
  readonly at: number;
  /** Mounted and open, under a layer pushed over it in the same slot. */
  readonly covered: boolean;
  /** The search this layer saw last while on top; what it keeps after. */
  readonly last: S;
  /** The layer's name in its slot (`exercise`, `coach`…). */
  readonly name: string;
  /** False while it plays its exit; a covered layer is still present. */
  readonly present: boolean;
  readonly view: V;
}

export interface Named<V> {
  /** The history depth the URL's layer was pushed at. */
  readonly at: number;
  readonly name: string;
  readonly view: V;
}

/** A retained layer's identity: its name and the depth it was pushed at. */
export function layerKey(layer: Pick<Named<unknown>, "at" | "name">): string {
  return `${layer.name}@${layer.at}`;
}

/**
 * The list after a render under `search` whose URL names `current` (or no
 * layer). Returns `entries` itself when nothing changed, so a caller can
 * compare by identity before writing state.
 */
export function retainLayers<V, S>(
  entries: readonly RetainedLayer<V, S>[],
  current: Named<V> | null,
  search: S
): readonly RetainedLayer<V, S>[] {
  let changed = false;
  const key = current === null ? null : layerKey(current);
  const next = entries.map((entry): RetainedLayer<V, S> => {
    if (current !== null && layerKey(entry) === key) {
      if (
        entry.present &&
        !entry.covered &&
        entry.last === search &&
        entry.view === current.view
      ) {
        return entry;
      }
      changed = true;
      return {
        at: entry.at,
        covered: false,
        last: search,
        name: entry.name,
        present: true,
        view: current.view,
      };
    }
    if (!entry.present) {
      return entry;
    }
    // Pushed under the layer the URL names now: kept, open, underneath.
    if (current !== null && entry.at < current.at) {
      if (entry.covered) {
        return entry;
      }
      changed = true;
      return { ...entry, covered: true };
    }
    changed = true;
    return { ...entry, covered: false, present: false };
  });
  if (current !== null && !entries.some((entry) => layerKey(entry) === key)) {
    changed = true;
    next.push({
      at: current.at,
      covered: false,
      last: search,
      name: current.name,
      present: true,
      view: current.view,
    });
  }
  return changed ? next : entries;
}

/** The list without the layer `key`, if it is leaving; a present layer is never dropped. */
export function dropExited<V, S>(
  entries: readonly RetainedLayer<V, S>[],
  key: string
): readonly RetainedLayer<V, S>[] {
  const at = entries.findIndex(
    (entry) => layerKey(entry) === key && !entry.present
  );
  if (at === -1) {
    return entries;
  }
  return [...entries.slice(0, at), ...entries.slice(at + 1)];
}
